import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api/client.js';
import { useCan } from '../../lib/auth/session.js';
import { useUpdateExtension } from '../../lib/api/hooks.js';
import { extKeys } from '../../lib/api/hooks-extension.js';
import { Section, Field, Toggle, FormActions } from '../../components/ui/Form.js';
import { Badge, ErrorBanner } from '../../components/ui/Primitives.js';
import { Flag } from '../../components/ui/Flag.js';
import {
  dpKeys, useCountries, usePolicies, showPrefix, barePrefix, type PolicyRules,
} from './dialPolicies.js';
import type { Extension } from '@arcnet/contracts';

interface Level extends PolicyRules { id: string; name: string }

interface Effective {
  home_country: string;
  home_prefix: string | null;
  internal_only: boolean;
  group: { id: string; name: string } | null;
  levels: { extension: Level | null; group: Level | null; tenant: Level | null };
  resolved: PolicyRules;
  test?: {
    destination: string; normalised: string;
    allowed: boolean; reason: string; explanation: string;
  };
}

type LevelKey = keyof Effective['levels'];
const LEVEL_WORD: Record<LevelKey, string> = {
  extension: 'extension', group: 'department', tenant: 'account',
};

/** Past this many, the rest of the countries are a count rather than a wall of flags. */
const COUNTRY_CHIPS = 24;

/**
 * What this extension may dial, and where that comes from.
 *
 * The seat subscribes to a policy, or takes its cost centre's or the account's.
 * The tab says which one decides, what it allows by each of the three methods,
 * and answers "would this number go through?" with the function the switch
 * runs. Three levels of inheritance is a model everybody is confident about and
 * many get wrong, so the answer is shown rather than left to be worked out.
 */
export function ExtensionRestrictionsTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const can = useCan();
  const writable = can('dialpermission:write');
  const effective = useQuery({
    queryKey: dpKeys.effective(tenantId, ext.id),
    queryFn: () => api.get<Effective>(`/v1/tenants/${tenantId}/extensions/${ext.id}/dial-policy`),
  });
  const policies = usePolicies(tenantId);
  const countries = useCountries();
  const save = useUpdateExtension(tenantId);
  const qc = useQueryClient();

  const e = effective.data;
  const ownId = e?.levels.extension?.id ?? '';
  const [policyId, setPolicyId] = useState(ownId);
  const [internalOnly, setInternalOnly] = useState(ext.internal_only);
  useEffect(() => { setPolicyId(ownId); }, [ownId]);
  useEffect(() => { setInternalOnly(ext.internal_only); }, [ext.internal_only]);

  if (effective.isLoading) return <div className="arc-skeleton" style={{ height: 180 }} />;
  if (!e) return <ErrorBanner error={effective.error} />;

  const r = e.resolved;
  const decider: LevelKey | null =
    e.levels.extension ? 'extension' : e.levels.group ? 'group' : e.levels.tenant ? 'tenant' : null;
  const deciding = decider ? e.levels[decider] : null;
  const n = r.allowed_countries.length;
  const abroad = r.allow_international && n > 0;

  const source = !deciding
    ? 'No policy is attached to it, its department or the account.'
    : decider === 'extension'
      ? `Its own policy decides: ${deciding.name}.`
      : decider === 'group'
        ? `Its department${e.group ? `, ${e.group.name},` : ''} decides with ${deciding.name}.`
        : `The account default decides: ${deciding.name}.`;

  const summary = e.internal_only
    ? { tone: 'muted' as const, label: 'internal only',
        text: 'Calls other extensions only. Every outside number is refused, whatever the policy allows.' }
    : {
        tone: abroad ? 'info' as const : 'muted' as const,
        label: abroad ? 'international' : 'national',
        text: `${!r.allow_international
          ? 'Dials national numbers only.'
          : n === 0
            ? 'Dials national numbers only: international is on, but no country is switched on.'
            : `Dials national numbers and ${n} ${n === 1 ? 'country' : 'countries'} abroad.`} ${source}`,
      };

  // Blocked prefixes come from every level, so each says where it came from.
  const blockedFrom = new Map<string, LevelKey[]>();
  for (const k of ['extension', 'group', 'tenant'] as const) {
    for (const p of e.levels[k]?.blocked_prefixes ?? []) {
      const key = barePrefix(p);
      blockedFrom.set(key, [...(blockedFrom.get(key) ?? []), k]);
    }
  }

  const names = new Map((countries.data?.data ?? []).map((c) => [c.iso2, c.name] as const));
  const nameOf = (iso: string) => names.get(iso) ?? iso;
  const allowedCountries = [...r.allowed_countries].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));

  const inherited = e.levels.group
    ? `${e.group?.name ?? 'department'}: ${e.levels.group.name}`
    : e.levels.tenant
      ? `account default: ${e.levels.tenant.name}`
      : 'no policy, national calls only';

  const dirty = policyId !== ownId || internalOnly !== ext.internal_only;
  const err = save.error instanceof ApiClientError ? save.error : null;
  const policyError = err?.fieldErrors['dial_policy_id'];

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-card-body">
          <div className="arc-row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Badge tone={summary.tone}>{summary.label}</Badge>
            <span style={{ lineHeight: 1.5 }}>{summary.text}</span>
          </div>

          <dl className="arc-kv" style={{ marginTop: 14 }}>
            <dt>Abroad</dt>
            <dd>
              {e.internal_only ? <span className="arc-subtle">refused</span>
                : !r.allow_international ? <span className="arc-subtle">off</span>
                : n === 0 ? <span className="arc-subtle">no country switched on</span>
                : (
                  <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {allowedCountries.slice(0, COUNTRY_CHIPS).map((iso) => (
                      <span key={iso} className="arc-badge arc-badge-muted" style={{ gap: 5 }}>
                        <Flag iso2={iso} />{nameOf(iso)}
                      </span>
                    ))}
                    {n > COUNTRY_CHIPS && <span className="arc-muted">and {n - COUNTRY_CHIPS} more</span>}
                    <span className="arc-subtle" style={{ fontSize: 12 }}>
                      {r.allow_premium ? 'high-risk destinations allowed' : 'high-risk destinations refused'}
                    </span>
                  </div>
                )}
            </dd>

            <dt>Blocked prefixes</dt>
            <dd>
              {blockedFrom.size === 0 ? <span className="arc-subtle">none</span> : (
                <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {[...blockedFrom].map(([p, from]) => (
                    <span key={p} className="arc-badge arc-badge-muted" style={{ gap: 5 }}>
                      <span className="arc-mono">{showPrefix(p, e.home_prefix)}</span>
                      <span className="arc-subtle">{from.map((k) => LEVEL_WORD[k]).join(', ')}</span>
                    </span>
                  ))}
                </div>
              )}
            </dd>

            <dt>Allowed prefixes</dt>
            <dd>
              {e.internal_only ? <span className="arc-subtle">not used</span>
                : r.allowed_prefixes.length === 0 ? <span className="arc-subtle">none</span>
                : (
                  <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {r.allowed_prefixes.map((p) => (
                      <span key={p} className="arc-badge arc-badge-muted arc-mono">
                        {showPrefix(p, e.home_prefix)}
                      </span>
                    ))}
                  </div>
                )}
            </dd>
          </dl>
        </div>
      </div>

      <Section title="Policy" columns={1}
        description="Subscribe this extension to a policy, or leave it to its department and the account. Policies are written under the account's Dialling restrictions.">
        <Field label="Dial policy" htmlFor="r-policy"
          {...(policyError ? { error: policyError } : {})}
          {...(policyId ? { hint: 'Replaces the department and account policies for this extension, apart from their blocked prefixes, which still apply.' } : {})}>
          <select id="r-policy" className="arc-select" value={policyId} disabled={!writable}
            onChange={(ev) => setPolicyId(ev.target.value)}>
            <option value="">Inherit — {inherited}</option>
            {(policies.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>
        <Toggle id="r-internal" label="Internal calls only"
          hint="Other extensions only: every outside number is refused, whatever the policy allows. For lobby, lift and warehouse phones."
          checked={internalOnly} onChange={setInternalOnly} disabled={!writable} />
      </Section>

      <DialTest tenantId={tenantId} extensionId={ext.id} />

      {writable && (
        <FormActions
          dirty={dirty} saving={save.isPending}
          onReset={() => { setPolicyId(ownId); setInternalOnly(ext.internal_only); }}
          onSave={() => save.mutate({
            id: ext.id,
            ...(policyId !== ownId ? { dial_policy_id: policyId || null } : {}),
            ...(internalOnly !== ext.internal_only ? { internal_only: internalOnly } : {}),
          }, {
            onSuccess: () => {
              void effective.refetch();
              // The seat itself, whose internal-only flag the form compares against.
              void qc.invalidateQueries({ queryKey: extKeys.extension(tenantId, ext.id) });
            },
          })}
        />
      )}
    </>
  );
}

/**
 * Would this number go through? Answered by the API with checkDestination --
 * the check the switch makes -- against this extension's saved rules.
 */
function DialTest({ tenantId, extensionId }: { tenantId: string; extensionId: string }) {
  const [draft, setDraft] = useState('');
  const [number, setNumber] = useState('');
  const result = useQuery({
    queryKey: [...dpKeys.effective(tenantId, extensionId), 'test', number],
    queryFn: () => api.get<Effective>(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/dial-policy?test=${encodeURIComponent(number)}`),
    enabled: number.length > 0,
  });
  const t = result.data?.test;

  return (
    <Section title="Would a number go through?" columns={1}
      description="Checked against this extension's saved rules, the way the switch checks a call it is about to place.">
      <form className="arc-row" style={{ gap: 8 }}
        onSubmit={(ev) => { ev.preventDefault(); setNumber(draft.trim()); }}>
        <input className="arc-input arc-mono" style={{ maxWidth: 220 }} value={draft} maxLength={24}
          onChange={(ev) => setDraft(ev.target.value)} placeholder="0821234567"
          aria-label="Number to check" />
        <button type="submit" className="arc-btn arc-btn-sm" disabled={!draft.trim()}>Check</button>
      </form>
      {result.error && <ErrorBanner error={result.error} />}
      {t && (
        <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', opacity: result.isFetching ? 0.6 : 1 }}>
          <Badge tone={t.allowed ? 'ok' : 'danger'}>{t.allowed ? 'goes through' : 'refused'}</Badge>
          <span>{t.explanation}</span>
          {t.normalised.length > 6 && (
            <span className="arc-subtle arc-mono" style={{ fontSize: 12 }}>checked as +{t.normalised}</span>
          )}
        </div>
      )}
    </Section>
  );
}
